<template>
  <v-navigation-drawer
    v-model="open"
    app
    dark
    width="264"
    mini-variant-width="76"
    :permanent="isDesktop"
    :temporary="!isDesktop"
    :mini-variant="isDesktop && mini"
    class="tn-sidebar"
    :class="{ 'tn-sidebar--mini': isDesktop && mini }"
  >
    <router-link :to="{ name: 'Home' }" class="tn-sidebar__brand" aria-label="Tanefer admin home">
      <img v-if="isDesktop && mini" src="../../assets/brand/icon-gold.png" alt="Tanefer" class="tn-sidebar__mark">
      <img v-else src="../../assets/brand/logo-white.png" alt="Tanefer" class="tn-sidebar__logo">
    </router-link>

    <nav class="tn-sidebar__nav" aria-label="Main navigation">
      <div v-for="section in sections" :key="section.title" class="tn-nav-section">
        <div v-if="!(isDesktop && mini)" class="tn-nav-section__title">{{ section.title }}</div>
        <div v-else class="tn-nav-section__rule"></div>

        <v-tooltip
          v-for="item in section.items"
          :key="item.route"
          right
          :disabled="!(isDesktop && mini)"
          open-delay="150"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-list-item
              v-bind="attrs"
              v-on="on"
              :to="{ name: item.route }"
              exact
              :input-value="isActive(item)"
              active-class="tn-nav-item--active"
              class="tn-nav-item"
              :class="{ 'tn-nav-item--active': isActive(item) }"
              @click="onNavigate"
            >
              <v-list-item-icon class="tn-nav-item__icon">
                <v-badge
                  v-if="item.badge && unreadCount > 0 && isDesktop && mini"
                  dot
                  color="orange"
                  overlap
                >
                  <v-icon size="20">{{ item.icon }}</v-icon>
                </v-badge>
                <v-icon v-else size="20">{{ item.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="tn-nav-item__label">{{ item.label }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="item.badge && unreadCount > 0" class="tn-nav-item__badge">
                <span class="tn-count">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
              </v-list-item-action>
            </v-list-item>
          </template>
          <span>{{ item.hint || item.label }}</span>
        </v-tooltip>
      </div>
    </nav>

    <template v-slot:append>
      <div v-if="user" class="tn-sidebar__user">
        <v-avatar size="36" color="orange" class="tn-sidebar__avatar">
          <span>{{ initials }}</span>
        </v-avatar>
        <div v-if="!(isDesktop && mini)" class="tn-sidebar__user-text">
          <div class="tn-sidebar__user-name">{{ user.name }}</div>
          <div v-if="user.email" class="tn-sidebar__user-email">{{ user.email }}</div>
        </div>
        <v-tooltip v-if="!(isDesktop && mini)" top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on" aria-label="Log out" @click="$emit('logout')">
              <v-icon size="20">mdi-logout</v-icon>
            </v-btn>
          </template>
          <span>Log out</span>
        </v-tooltip>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
const sections = [
  {
    title: 'Overview',
    items: [
      { route: 'Home', label: 'Dashboard', icon: 'mdi-view-dashboard-outline' }
    ]
  },
  {
    title: 'Catalog',
    items: [
      { route: 'tripsTours', label: 'Packages', icon: 'mdi-beach' },
      { route: 'tripsCities', label: 'Trip Cities', icon: 'mdi-city-variant-outline' },
      { route: 'hotels', label: 'Hotels', icon: 'mdi-bed-outline' },
      { route: 'seasons', label: 'Hotel Seasons', icon: 'mdi-calendar-range' },
      { route: 'activities', label: 'Adventures', icon: 'mdi-hiking' },
      { route: 'cruises', label: 'Cruises', icon: 'mdi-ferry' }
    ]
  },
  {
    title: 'Bookings',
    items: [
      { route: 'tripBookings', label: 'Packages', hint: 'Package bookings', icon: 'mdi-ticket-confirmation-outline' },
      { route: 'adventuresBookings', label: 'Adventures', hint: 'Adventure bookings', icon: 'mdi-compass-outline' },
      { route: 'cruisesBookings', label: 'Cruises', hint: 'Cruise bookings', icon: 'mdi-sail-boat' },
      { route: 'hotelsBookings', label: 'Hotels', hint: 'Hotel bookings', icon: 'mdi-office-building-outline' },
      { route: 'hotelRefunds', label: 'Refunds', hint: 'Hotel refunds', icon: 'mdi-cash-refund' }
    ]
  },
  {
    title: 'Customers',
    items: [
      { route: 'registeredUsers', label: 'Registered Users', icon: 'mdi-account-group-outline' },
      { route: 'notifications', label: 'Notifications', icon: 'mdi-bell-outline', badge: true }
    ]
  },
  {
    title: 'Settings',
    items: [
      { route: 'settingFees', label: 'Fee Settings', icon: 'mdi-cash-multiple' },
      { route: 'appContent', label: 'App content', hint: 'Mobile app banners and onboarding', icon: 'mdi-cellphone-text' },
      { route: 'appPages', label: 'App pages', hint: 'Privacy, terms, FAQ and contact in the app', icon: 'mdi-file-document-multiple-outline' },
      { route: 'settingSeo', label: 'SEO', icon: 'mdi-web' }
    ]
  }
]

export default {
  name: 'AppSidebar',
  props: {
    value: { type: Boolean, default: false },
    mini: { type: Boolean, default: false },
    unreadCount: { type: Number, default: 0 }
  },
  data () {
    return { sections }
  },
  computed: {
    isDesktop () {
      return this.$vuetify.breakpoint.mdAndUp
    },
    open: {
      get () { return this.isDesktop || this.value },
      set (val) { if (!this.isDesktop) this.$emit('input', val) }
    },
    user () {
      return this.$store.state.user
    },
    initials () {
      const name = (this.user && this.user.name) || ''
      return name.split(' ').filter(Boolean).slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('') || 'A'
    }
  },
  methods: {
    // Detail pages (hotel/5, package/booking/9...) highlight their list via meta.nav.
    isActive (item) {
      return this.$route.name === item.route || this.$route.meta.nav === item.route
    },
    onNavigate () {
      if (!this.isDesktop) this.$emit('input', false)
    }
  }
}
</script>

<style lang="scss">
.tn-sidebar.v-navigation-drawer {
  background: linear-gradient(180deg, #0F1B2D 0%, #0B1423 100%) !important;
  border-right: 0;

  .v-navigation-drawer__border {
    display: none;
  }

  .v-navigation-drawer__content {
    display: flex;
    flex-direction: column;
    scrollbar-color: rgba(255, 255, 255, .18) transparent;

    &::-webkit-scrollbar-thumb {
      background-color: rgba(255, 255, 255, .18);
    }
  }
}

.tn-sidebar__brand {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  height: 64px;
  padding: 0 24px;
  border-bottom: 1px solid rgba(255, 255, 255, .07);
}

.tn-sidebar__logo {
  height: 30px;
  width: auto;
  display: block;
}

.tn-sidebar__mark {
  width: 28px;
  height: 28px;
  display: block;
  margin: 0 auto;
}

.tn-sidebar--mini .tn-sidebar__brand {
  padding: 0;
  justify-content: center;
}

.tn-sidebar__nav {
  flex: 1 1 auto;
  padding: 8px 0 16px;
}

.tn-nav-section__title {
  padding: 16px 24px 6px;
  color: rgba(255, 255, 255, .42);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.tn-nav-section__rule {
  height: 1px;
  margin: 10px 18px;
  background: rgba(255, 255, 255, .08);
}

.tn-nav-section:first-child .tn-nav-section__rule {
  visibility: hidden;
}

.tn-sidebar .v-list-item.tn-nav-item {
  position: relative;
  min-height: 38px;
  margin: 1px 12px;
  padding: 0 12px;
  border-radius: 8px;
  color: rgba(255, 255, 255, .74) !important;

  &::before {
    border-radius: 8px;
  }

  &:hover::before {
    opacity: .06;
  }

  .tn-nav-item__icon {
    margin: 0 14px 0 0 !important;
    align-self: center;

    .v-icon {
      color: rgba(255, 255, 255, .62);
    }
  }

  .tn-nav-item__label {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
  }

  .tn-nav-item__badge {
    margin: 0 !important;
    min-width: 0;
  }
}

.tn-sidebar .v-list-item.tn-nav-item--active {
  color: #FFFFFF !important;

  &::before {
    opacity: 0 !important;
  }

  background: rgba(255, 255, 255, .09);

  .tn-nav-item__icon .v-icon {
    color: var(--v-orange-base);
  }

  .tn-nav-item__label {
    font-weight: 600;
  }

  &::after {
    content: '';
    position: absolute;
    left: -12px;
    top: 8px;
    bottom: 8px;
    min-height: 0;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: var(--v-orange-base);
  }
}

.tn-sidebar--mini .v-list-item.tn-nav-item {
  justify-content: center;
  margin: 2px 14px;
  padding: 0;

  .tn-nav-item__icon {
    margin: 0 !important;
  }
}

.tn-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: var(--v-orange-base);
  color: #FFFFFF;
  font-size: 11px;
  font-weight: 700;
}

.tn-sidebar__user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px 14px 20px;
  border-top: 1px solid rgba(255, 255, 255, .07);
}

.tn-sidebar--mini .tn-sidebar__user {
  justify-content: center;
  padding: 14px 0;
}

.tn-sidebar__avatar span {
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 700;
}

.tn-sidebar__user-text {
  flex: 1 1 auto;
  min-width: 0;
}

.tn-sidebar__user-name,
.tn-sidebar__user-email {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.tn-sidebar__user-name {
  color: #FFFFFF;
  font-size: 13.5px;
  font-weight: 600;
}

.tn-sidebar__user-email {
  color: rgba(255, 255, 255, .5);
  font-size: 12px;
}
</style>
